/**
 * EmailJS Service
 * Handles all email functionality using EmailJS with a single generic template
 */

import emailjs from '@emailjs/browser';

// EmailJS configuration - these should be set in environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Initialize EmailJS with public key
if (EMAILJS_PUBLIC_KEY) {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

/**
 * Generate email content for contact form
 * @param {object} contactData - Contact form data
 * @returns {object} - Formatted email data
 */
const generateContactEmail = (contactData) => {
  const subject = `Contact Form Submission from ${contactData.name}`;
  const emailBody = `
Contact Form Submission

From: ${contactData.name}
Email: ${contactData.email}
Subject: ${contactData.subject || 'Contact Form Submission'}

Message:
${contactData.message}

---
This email was sent from the Birmingham Go Club website contact form.
  `.trim();

  return {
    subject,
    email_body: emailBody,
    from_name: contactData.name,
    from_email: contactData.email,
    to_email: 'steelcitygoclub@gmail.com'
  };
};

/**
 * Generate email content for membership application
 * @param {object} membershipData - Membership form data
 * @returns {object} - Formatted email data
 */
const generateMembershipEmail = (membershipData) => {
  const subject = `New Membership Application - ${membershipData.name}`;
  const emailBody = `
New Membership Application

Name: ${membershipData.name}
Email: ${membershipData.email}
Phone: ${membershipData.phone || 'Not provided'}
Experience Level: ${membershipData.experience}
How they heard about us: ${membershipData.howHeard || 'Not specified'}
Newsletter subscription: ${membershipData.newsletter ? 'Yes' : 'No'}

---
This application was submitted through the Birmingham Go Club website.
  `.trim();

  return {
    subject,
    email_body: emailBody,
    from_name: membershipData.name,
    from_email: membershipData.email,
    to_email: 'steelcitygoclub@gmail.com'
  };
};

/**
 * Generate email content for newsletter subscription
 * @param {object} newsletterData - Newsletter data with email
 * @returns {object} - Formatted email data
 */
const generateNewsletterEmail = (newsletterData) => {
  const subject = 'New Newsletter Subscription';
  const emailBody = `
New Newsletter Subscription

Email: ${newsletterData.email}

---
This subscription was submitted through the Birmingham Go Club website.
  `.trim();

  return {
    subject,
    email_body: emailBody,
    from_name: 'Website Visitor',
    from_email: newsletterData.email,
    to_email: 'steelcitygoclub@gmail.com'
  };
};

/**
 * Send email using the generic EmailJS template
 * @param {object} emailData - Formatted email data
 * @returns {Promise} - EmailJS response
 */
const sendEmail = async (emailData) => {
  try {
    // Check for missing configuration with more specific error messages
    if (!EMAILJS_SERVICE_ID) {
      throw new Error('Email service is not configured. Missing Service ID. Please contact the website administrator.');
    }
    if (!EMAILJS_TEMPLATE_ID) {
      throw new Error('Email service is not configured. Missing Template ID. Please contact the website administrator.');
    }
    if (!EMAILJS_PUBLIC_KEY) {
      throw new Error('Email service is not configured. Missing Public Key. Please contact the website administrator.');
    }

    console.log('Attempting to send email with EmailJS...');
    console.log('Service ID:', EMAILJS_SERVICE_ID ? 'Set' : 'Missing');
    console.log('Template ID:', EMAILJS_TEMPLATE_ID ? 'Set' : 'Missing');
    console.log('Public Key:', EMAILJS_PUBLIC_KEY ? 'Set' : 'Missing');

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      emailData
    );

    console.log('Email sent successfully:', response);
    return { success: true, messageId: response.text };
  } catch (error) {
    console.error('Error sending email:', error);
    
    // Provide more specific error messages based on the error type
    if (error.message && error.message.includes('not configured')) {
      // Our custom configuration error
      throw error;
    } else if (error.text && error.text.includes('Invalid service ID')) {
      throw new Error('Email service configuration error. Please contact the website administrator.');
    } else if (error.text && error.text.includes('Invalid template ID')) {
      throw new Error('Email template not found. Please contact the website administrator.');
    } else if (error.text && error.text.includes('Invalid public key')) {
      throw new Error('Email service authentication failed. Please contact the website administrator.');
    } else if (error.text && error.text.includes('rate limit')) {
      throw new Error('Too many emails sent recently. Please try again in a few minutes.');
    } else {
      // Generic fallback error
      throw new Error('Failed to send email. Please try again or contact us directly at steelcitygoclub@gmail.com');
    }
  }
};

/**
 * Send contact form email using EmailJS
 * @param {object} contactData - Contact form data
 * @returns {Promise} - EmailJS response
 */
export const submitContactForm = async (contactData) => {
  try {
    const emailData = generateContactEmail(contactData);
    await sendEmail(emailData);
    return { success: true, message: 'Contact form submitted successfully' };
  } catch (error) {
    console.error('Error sending contact form email:', error);
    throw new Error('Failed to send contact form. Please try again.');
  }
};

/**
 * Send membership application email using EmailJS
 * @param {object} membershipData - Membership form data
 * @returns {Promise} - EmailJS response
 */
export const submitMembershipApplication = async (membershipData) => {
  try {
    const emailData = generateMembershipEmail(membershipData);
    await sendEmail(emailData);
    return { success: true, message: 'Membership application submitted successfully' };
  } catch (error) {
    console.error('Error sending membership application email:', error);
    throw new Error('Failed to send membership application. Please try again.');
  }
};

/**
 * Send newsletter subscription email using EmailJS
 * @param {object} newsletterData - Newsletter data with email
 * @returns {Promise} - EmailJS response
 */
export const subscribeToNewsletter = async (newsletterData) => {
  try {
    const emailData = generateNewsletterEmail(newsletterData);
    await sendEmail(emailData);
    return { success: true, message: 'Newsletter subscription successful' };
  } catch (error) {
    console.error('Error sending newsletter subscription email:', error);
    throw new Error('Failed to subscribe to newsletter. Please try again.');
  }
};

// Export default object with all methods
const emailService = {
  submitContactForm,
  submitMembershipApplication,
  subscribeToNewsletter
};

export default emailService; 